import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { useEnteredView } from '@fiverr-private/hooks';
import { triggerSignUp } from '@fiverr-private/identification_triggers';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import pathfinder from '@fiverr-private/pathfinder';
import { Heading } from '../ui/Heading';
import { translate } from '../../../shared/helpers';
import { sendBiankaImpression } from '../../../shared/biEvents';
import { EVENT_NAMES } from '../../../shared/biEvents/eventNames';
import { sendMixpanelEvent } from '../utils/mixpanel';
import { MIXPANEL_EVENTS } from '../utils/eventNames';
import { TESTIMONIALS_ITEMS, TestimonialItem } from './constants';
import TestimonialCard from './TestimonialCard';
import TestimonialModal from './TestimonialModal';

import * as styles from './styles.ve.css';

const Testimonials = () => {
  const container = useRef<HTMLDivElement>(null);
  const eventTrackingRef = useRef<HTMLDivElement>(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState<TestimonialItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasLoggedImpression = useRef(false);

  useEnteredView(eventTrackingRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.EXPERTS_CAROUSEL_IS_SHOWN,
        componentName: 'experts_carousel',
        componentText: 'ProSection',
      });

      if (!hasLoggedImpression.current) {
        sendMixpanelEvent({
          eventName: MIXPANEL_EVENTS.TESTIMONIALS_IS_SHOWN,
        });
        hasLoggedImpression.current = true;
      }
    },
  });

  const pathToJoin = pathfinder('join', undefined, { query: { source: 'testimonials_cta' } });

  const onSignUpButtonClick = async (event) => {
    event.preventDefault();

    sendMixpanelEvent({
      eventName: MIXPANEL_EVENTS.CLICKED_CTA_BUTTON,
      clickedElement: 'Get Started',
      elementSource: 'Testimonials',
    });

    await triggerSignUp({
      modalOptions: {
        triggerType: 'user_trigger',
        triggerId: 'hplo_get_started',
      },
    });
  };

  const handleCardClick = (testimonial: TestimonialItem, index: number) => {
    sendMixpanelEvent({
      eventName: MIXPANEL_EVENTS.CLICKED_UGC_VIDEO,
      clickedElement: testimonial.name,
      position: index + 1,
    });

    setSelectedTestimonial(testimonial);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedTestimonial(null);
  };

  return (
    <>
      <Container ref={container}>
        <Stack direction="column" className={styles.wrapperStyles} alignItems="center">
          <Heading alignSelf="flexStart">{translate('v17.testimonials.heading')}</Heading>

          <Container ref={eventTrackingRef} className={styles.carouselContainer}>
            <Stack className={styles.cardsWrapper} direction="row">
              {TESTIMONIALS_ITEMS.map((item, index) => (
                <Container key={item.key} className={styles.cardWrapper}>
                  <TestimonialCard
                    imageSrc={item.imageSrc}
                    name={item.name}
                    position={item.position}
                    quote={item.quote}
                    onClick={() => handleCardClick(item, index)}
                  />
                </Container>
              ))}
            </Stack>
          </Container>

          <Button
            href={pathToJoin}
            colorScheme="white"
            className={classnames('btn-signup', styles.buttonStyles)}
            onClick={onSignUpButtonClick}
          >
            {translate('v17.testimonials.cta')}
          </Button>
        </Stack>
      </Container>

      {selectedTestimonial ? (
        <TestimonialModal testimonial={selectedTestimonial} isOpen={isModalOpen} onClose={handleClose} />
      ) : null}
    </>
  );
};

export default Testimonials;
